/**
 * PATH: packages/email-hub/src/feature/hooks/useInboxHotkeys.ts
 * FILE_ID: EMAILHUB-HOOKS-INBOX-HOTKEYS-001
 * PURPOSE: The single inbox keydown handler + hover tracking, resolving keys
 *   through the EMAIL_SHORTCUTS registry (extracted from InboxPage on contact,
 *   FEAT-2026-07-06-EMAIL-SHORTCUTS).
 * WHY: Landmines carried over verbatim from the inline handler —
 *   BUG-2026-06-11-COMPOSE-HOTKEY-HIJACK (contentEditable/compose-dock guard),
 *   BUG-2026-05-21-EMAIL-C-HOTKEY (hovered wins over selected). `mod+x` is the
 *   only chord handled; all other mod combos bail so ⌘C copy stays native.
 *   `c` = COMPLETE (archive + complete:true flips the linked card DONE);
 *   plain archive is `e` (operator 2026-07-06: "c should have been complete…
 *   leave d alone… cmd x should be delete").
 */
import { useEffect, useRef, type MutableRefObject } from 'react'

import type { Email } from './useEmails.types'
import { siblingDuplicateIds, type DedupedEmail } from '../lib/dedup'
import { resolveTargetAfterRemovals } from '../email-reader/navigation'
import {
  matchEmailShortcut,
  EMAIL_PRIORITY_BY_ID,
} from '../shortcuts/registry'

type ToastKind = 'success' | 'error' | 'warning' | 'info'

export interface UseInboxHotkeysDeps {
  /** Bail entirely while the shortcuts panel modal is open. */
  panelOpenRef: MutableRefObject<boolean>
  /** The deduped, currently rendered email list. */
  emailsRef: MutableRefObject<DedupedEmail[]>
  selectedEmailId: string | null
  setSelectedEmailId: (id: string | null) => void
  /** Multi-selection (checkbox) ids. */
  selectedIdsRef: MutableRefObject<Set<string>>
  setSelectedIds: (ids: Set<string>) => void
  /** Pending arrow-nav mark-read settle timer (cleared on Escape). */
  arrowReadTimerRef: MutableRefObject<number | null>
  scrollRowIntoView: (id: string) => void
  markRowReadDebounced: (id: string) => void
  onOpenEmail: (id: string) => void
  onCompose: () => void
  onToggleShortcutsPanel: () => void
  /** Open Quick Triage palette for the hovered/selected email (B-01). */
  onOpenQuickTriage: (emailId: string) => void
  /** Open the allocate-to-Kanban-board picker (shortcut A). */
  onAllocateToBoard: (emailId: string) => void
  bulkAction: (
    ids: string[],
    action: string,
    extras?: Record<string, unknown>,
  ) => Promise<unknown>
  archive: (
    id: string,
    prev?: { folder?: string; isRead?: boolean; isStarred?: boolean; complete?: boolean },
  ) => Promise<unknown>
  toggleStar: (id: string, isStarred: boolean) => Promise<unknown>
  toggleRead: (id: string, isRead: boolean) => Promise<unknown>
  updateEmailLocal: (id: string, patch: Partial<Email>) => void
  removeEmailsLocal: (ids: string[]) => void
  refetch: () => void
  toast: (message: string, kind?: ToastKind) => void
  syncDuplicates: (ids: string[], action: string) => Promise<void> | void
  /** PATCH the linked Kanban card's priority (1 Normal / 2 High / 3 Urgent). */
  setCardPriority: (cardId: string, priority: number) => Promise<unknown>
}

/**
 * useInboxHotkeys(deps) → void. Installs (1) a document mousemove delegator
 * lifting the hovered row off `[data-email-id]`, and (2) one window keydown
 * handler that resolves keys via matchEmailShortcut and dispatches to the
 * supplied actions. Hovered email wins over the keyboard-selected one for
 * single-target actions (the operator's literal "while hovering" contract).
 */
export function useInboxHotkeys(deps: UseInboxHotkeysDeps): void {
  // Rows can't take a hover prop (memoised EmailRow) so we lift via event
  // delegation rather than plumbing onHoverChange through every row.
  const hoveredEmailIdRef = useRef<string | null>(null)
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const t = e.target
      if (!(t instanceof Element)) {
        hoveredEmailIdRef.current = null
        return
      }
      const row = t.closest('[data-email-id]') as HTMLElement | null
      hoveredEmailIdRef.current = row?.dataset.emailId ?? null
    }
    document.addEventListener('mousemove', onMove)
    return () => document.removeEventListener('mousemove', onMove)
  }, [])

  // Keep a live deps ref so the keydown listener is installed ONCE and always
  // dispatches against fresh state/actions (mirrors the Kanban handler's ref
  // pattern; avoids listener churn on every selection change).
  const depsRef = useRef(deps)
  depsRef.current = deps

  useEffect(() => {
    const onAllocateKey = (e: KeyboardEvent) => {
      const d = depsRef.current
      if (d.panelOpenRef.current) return
      if (e.altKey || e.metaKey || e.ctrlKey) return
      if (e.key.toLowerCase() !== 'a') return
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) return
      if (e.target instanceof HTMLElement) {
        if (e.target.isContentEditable) return
        if (e.target.closest('[data-compose-dock]')) return
      }
      const singleTarget = hoveredEmailIdRef.current ?? d.selectedEmailId
      if (!singleTarget) return
      e.preventDefault()
      e.stopPropagation()
      d.onAllocateToBoard(singleTarget)
    }
    const handler = (e: KeyboardEvent) => {
      const d = depsRef.current
      // The keyboard-shortcuts panel is a modal over the inbox; while it's
      // open the operator is reading/rebinding keys, not triaging. Bail so a
      // key typed there (incl. a capture in ShortcutCustomizer) can't archive
      // or junk the email sitting behind the modal.
      if (d.panelOpenRef.current) return
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      // BUG-2026-06-11-COMPOSE-HOTKEY-HIJACK: the compose dock's rich-text
      // body is a contentEditable <div> floating OVER the inbox. Without this
      // guard, typing "c" archived the selected email. Also bail for anything
      // rendered inside the compose dock portal (selects, buttons, chips).
      if (e.target instanceof HTMLElement) {
        if (e.target.isContentEditable) return
        if (e.target.closest('[data-compose-dock]')) return
      }
      if (e.altKey) return
      const mod = e.metaKey || e.ctrlKey
      const shortcut = matchEmailShortcut({ key: e.key, mod })
      // Unmatched mod chords (⌘C copy, ⌘A select-all…) stay native.
      if (!shortcut) return

      const list = d.emailsRef.current
      const idx = list.findIndex((em) => em.id === d.selectedEmailId)
      // Hovered email wins over the keyboard cursor for single-target
      // actions (BUG-2026-05-21-EMAIL-C-HOTKEY — "while hovering").
      const singleTarget = hoveredEmailIdRef.current ?? d.selectedEmailId
      const findEmail = (id: string | null) =>
        id ? list.find((em) => em.id === id) : undefined
      const advanceAfterRemoval = (removedIds: string[]) => {
        if (
          !d.selectedEmailId
          || !removedIds.includes(d.selectedEmailId)
        ) {
          return
        }
        d.setSelectedEmailId(
          resolveTargetAfterRemovals(
            list.map((email) => email.id),
            d.selectedEmailId,
            new Set(removedIds),
          ),
        )
      }

      switch (shortcut.id) {
        case 'move-down': {
          e.preventDefault()
          let nextId: string | null = null
          if (idx < list.length - 1) nextId = list[idx + 1]?.id ?? null
          else if (idx === -1 && list.length > 0) nextId = list[0].id
          if (nextId) {
            d.setSelectedEmailId(nextId)
            d.scrollRowIntoView(nextId)
            d.markRowReadDebounced(nextId)
          }
          return
        }
        case 'move-up': {
          e.preventDefault()
          if (idx > 0) {
            const nextId = list[idx - 1].id
            d.setSelectedEmailId(nextId)
            d.scrollRowIntoView(nextId)
            d.markRowReadDebounced(nextId)
          }
          return
        }
        case 'deselect': {
          // Bailed before the read-settle fired — don't mark it read.
          if (d.arrowReadTimerRef.current) window.clearTimeout(d.arrowReadTimerRef.current)
          d.setSelectedEmailId(null)
          return
        }
        case 'open': {
          if (d.selectedEmailId) d.onOpenEmail(d.selectedEmailId)
          return
        }
        case 'compose': {
          e.preventDefault()
          d.onCompose()
          return
        }
        case 'focus-search': {
          const input = document.querySelector<HTMLInputElement>(
            'input[aria-label="Search emails"]',
          )
          if (input) {
            e.preventDefault()
            input.focus()
          }
          return
        }
        case 'cheat-sheet': {
          e.preventDefault()
          d.onToggleShortcutsPanel()
          return
        }
        case 'quick-triage': {
          if (!singleTarget) return
          e.preventDefault()
          d.onOpenQuickTriage(singleTarget)
          return
        }
        case 'allocate-board': {
          // Capture listener above owns A so the open-email reader does not
          // also open a second picker. This branch covers a bubble-only event.
          if (e.defaultPrevented) return
          if (!singleTarget) return
          e.preventDefault()
          e.stopPropagation()
          d.onAllocateToBoard(singleTarget)
          return
        }
        case 'complete': {
          // c = COMPLETE: archive + `complete: true` (flips the linked Kanban
          // card to its DONE lane) + optimistic hide — the operator's "done
          // and hides it from the view" contract. Plain archive lives on `e`.
          if (!singleTarget) return
          e.preventDefault()
          const em = findEmail(singleTarget)
          advanceAfterRemoval([singleTarget])
          d.removeEmailsLocal([singleTarget])
          d.archive(singleTarget, {
            folder: em?.folder,
            isRead: em?.is_read,
            isStarred: em?.is_starred,
            complete: true,
          })
            .then(() => d.refetch())
            .catch(() => {
              d.toast('Couldn\u2019t complete - try again.', 'error')
              d.refetch()
            })
          void d.syncDuplicates(siblingDuplicateIds(list, [singleTarget]), 'archive')
          return
        }
        case 'archive': {
          // e = plain archive (no card flip) — the pre-2026-07-06 `c`.
          if (!singleTarget) return
          e.preventDefault()
          advanceAfterRemoval([singleTarget])
          d.removeEmailsLocal([singleTarget])
          d.bulkAction([singleTarget], 'archive')
            .then(() => d.refetch())
            .catch(() => d.toast('Couldn\u2019t archive - try again.', 'error'))
          void d.syncDuplicates(siblingDuplicateIds(list, [singleTarget]), 'archive')
          return
        }
        case 'done':
        case 'junk':
        case 'delete': {
          // Act on the WHOLE multi-selection when one exists, else the
          // hovered/selected row. ``junk`` soft-bins + stamps category +
          // audits (recoverable); ``delete`` is the plain soft-bin
          // (status='binned'); ``archive`` (Done) hides. Rows drop from the
          // deck IMMEDIATELY so the disposition reads as instant; failure
          // restores the selection and refetches (self-heal).
          const sel = d.selectedIdsRef.current
          const targetIds = sel.size > 0 ? Array.from(sel) : singleTarget ? [singleTarget] : []
          if (targetIds.length === 0) return
          e.preventDefault()
          const action =
            shortcut.id === 'junk' ? 'junk' : shortcut.id === 'delete' ? 'delete' : 'archive'
          const failMsg =
            shortcut.id === 'junk'
              ? 'Couldn\u2019t junk - try again.'
              : shortcut.id === 'delete'
                ? 'Couldn\u2019t delete - try again.'
                : 'Couldn\u2019t mark done - try again.'
          const prevSel = new Set(sel)
          advanceAfterRemoval(targetIds)
          d.removeEmailsLocal(targetIds)
          if (sel.size > 0) d.setSelectedIds(new Set())
          void d.syncDuplicates(siblingDuplicateIds(list, targetIds), action)
          d.bulkAction(targetIds, action)
            .then(() => d.refetch())
            .catch(() => {
              if (prevSel.size > 0) d.setSelectedIds(prevSel)
              d.toast(failMsg, 'error')
              d.refetch()
            })
          return
        }
        case 'toggle-read': {
          if (!singleTarget) return
          const em = findEmail(singleTarget)
          if (!em) return
          e.preventDefault()
          d.updateEmailLocal(singleTarget, { is_read: !em.is_read } as Partial<Email>)
          d.toggleRead(singleTarget, !!em.is_read).catch(() => d.refetch())
          return
        }
        case 'toggle-pin': {
          if (!singleTarget) return
          const em = findEmail(singleTarget)
          if (!em) return
          e.preventDefault()
          d.updateEmailLocal(singleTarget, { is_starred: !em.is_starred } as Partial<Email>)
          d.toggleStar(singleTarget, !!em.is_starred).catch(() => d.refetch())
          return
        }
        case 'priority-normal':
        case 'priority-high':
        case 'priority-urgent': {
          if (!singleTarget) return
          const em = findEmail(singleTarget)
          const cardId = em?.allocation_summary?.card_id
          const meta = EMAIL_PRIORITY_BY_ID[shortcut.id]
          if (!meta) return
          if (!cardId) {
            // Honest no-op feedback — a silent 1/2/3 reads as "broken".
            d.toast('This email isn\u2019t on a board yet - no card to prioritise.', 'info')
            return
          }
          e.preventDefault()
          d.setCardPriority(String(cardId), meta.value)
            .then(() => d.toast(`Priority set to ${meta.label}`, 'success'))
            .catch(() => d.toast('Couldn\u2019t set the priority - try again.', 'error'))
          return
        }
      }
    }
    window.addEventListener('keydown', onAllocateKey, true)
    window.addEventListener('keydown', handler)
    return () => {
      window.removeEventListener('keydown', onAllocateKey, true)
      window.removeEventListener('keydown', handler)
    }
  }, [])
}
